/**
 * Realtime wire types — the server → client envelope and the seven event
 * payloads. [milestone M5-T6]
 *
 * Mirrors the backend's `hub/app/realtime/events.py` (envelope) and
 * `hub/app/realtime/payloads.py` (per-type payload). Zod schemas (not raw
 * `interface`s) so a malformed event from a future server version surfaces as
 * a parse error in one place instead of corrupting the cache.
 *
 * The envelope is `{type, seq, payload}` exactly — anything else (the auth
 * frame, the pong reply, the gap notice) flows through a separate union.
 *
 * SERVER FRAME SHAPES (as read from `hub/app/api/ws.py` and `events.py`):
 *
 *   - **Auth frame (client → server, first frame):**
 *       { "type": "auth", "token": "<jwt>", "last_seq": <int|null> }
 *     Any deviation closes the socket with code 1008.
 *
 *   - **Pong (server → client, in reply to ping):**
 *       { "type": "pong" }
 *     No `seq`, no `payload` — not an Event envelope.
 *
 *   - **Gap notice (server → client, on reconnect-replay when the buffer
 *     trimmed past the client's checkpoint):**
 *       { "type": "replay.gap", "detail": "refetch_required" }
 *     Note the name: it is `replay.gap`, NOT `gap_too_large` (that's the
 *     internal variable name in the Python; the wire frame is `replay.gap`).
 *
 *   - **Event envelope (server → client, live + replay):**
 *       { "type": <EventType>, "seq": <int>, "payload": <type-specific> }
 *
 *   - **Client → server frames (subscribe / unsubscribe / typing / viewing /
 *     ping):** the WS is read-only by contract; we only ever emit one of these
 *     five shapes plus the auth frame. See `connection.ts`.
 */

import { z } from 'zod';

import { accountAttentionSchema } from '@/features/accounts/accounts.api';
import {
  conversationOutSchema,
  lockOutSchema,
} from '@/features/conversations/conversations.api';
import { messageOutSchema, reactionOutSchema } from '@/features/chat/chat.api';

// --- Per-event payload schemas --------------------------------------------

/** `message.new` — a newly persisted message: customer inbound, a console
 *  send's server row, a phone-sent outbound (direction='outbound' with
 *  sender_agent_id null — the operator replied from the paired phone), or a
 *  RESENT row (`POST /messages/{id}/resend` — carries
 *  `resend_of_message_id`, which rides in via `messageOutSchema` below; the
 *  payload reuses that schema wholesale, so MessageOut additions land here
 *  automatically). Backend builder: `message_new_payload(message)` in
 *  `payloads.py`. */
export const messageNewPayloadSchema = z.object({
  conversation_id: z.string().uuid(),
  message: messageOutSchema,
});

/** `message.status` — an outbound message's delivery state changed: the
 *  send / resend result, a reconnect-flush backfill, the hub's startup sweep
 *  of orphaned `queued` rows, or a WhatsApp receipt (`delivered` / `read` /
 *  post-send error). Backend builder: `message_status_payload(...)`.
 *
 *  `delivery_status` stays a plain string (vocabulary: `DELIVERY_STATUSES`
 *  in `features/chat/delivery-status.ts`). The three 2026-09 side fields
 *  (spec 00 §3.2 WS `message.status` delta) mirror `MessageOut`:
 *  `delivery_error` (`null` clears it), `delivered_at`, `read_at`.
 *  `.nullable().optional()`: a pre-0015 hub omits them, and the projector
 *  leaves the cached values alone when a key is absent. */
export const messageStatusPayloadSchema = z.object({
  message_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  delivery_status: z.string(),
  platform_message_id: z.string().nullable(),
  delivery_error: z.string().nullable().optional(),
  delivered_at: z.string().nullable().optional(),
  read_at: z.string().nullable().optional(),
});

/** `message.reaction` — one message's reaction set changed (customer/phone
 *  reaction ingested, or an agent reacted via the API). `reactions` is the
 *  FULL current list, replaced wholesale on the client — idempotent and
 *  immune to event reordering (the newest event always wins outright).
 *  Backend builder: `message_reaction_payload(...)`. */
export const messageReactionPayloadSchema = z.object({
  conversation_id: z.string().uuid(),
  message_id: z.string().uuid(),
  reactions: z.array(reactionOutSchema),
});

/** `conversation.updated` — the whole `ConversationOut`. Used by the backend
 *  after assign / read / lifecycle change. */
export const conversationUpdatedPayloadSchema = conversationOutSchema;

/** `conversation.lock` — discriminated by the `locked` boolean. */
export const conversationLockLockedPayloadSchema = z.object({
  conversation_id: z.string().uuid(),
  locked: z.literal(true),
  lock: lockOutSchema,
});

export const conversationLockReleasedPayloadSchema = z.object({
  conversation_id: z.string().uuid(),
  locked: z.literal(false),
  released_by_agent_id: z.string().uuid(),
});

export const conversationLockPayloadSchema = z.discriminatedUnion('locked', [
  conversationLockLockedPayloadSchema,
  conversationLockReleasedPayloadSchema,
]);

/** `presence` — typing / viewing / online / offline. Phase 1 just dispatches
 *  on type; consumers haven't shipped yet. Schema kept open. */
export const presencePayloadSchema = z.object({
  agent_id: z.string().uuid(),
  agent_display_name: z.string().nullable(),
  activity: z.string(),
  conversation_id: z.string().uuid().nullable(),
});

/** `account.status` — an account's gateway state, detail or attention
 *  changed. Backend builder: `account_status_payload(...)`.
 *
 *    - `account_id` — the gateway EXTERNAL id (`wa_…`), not the row UUID.
 *    - `state` — the `accounts.status` value (`ck_accounts_status`:
 *      connected / connecting / qr_required / down / banned), a plain
 *      string for forward compat (see `ACCOUNT_STATUSES`).
 *    - `detail` — the gateway's status detail (`str | None` on the hub's
 *      adapter `AccountStatus`); lands on `Account.status_detail`.
 *    - `status_since` / `attention` — the 2026-09 additions (spec 00 §3.2
 *      WS `account.status` delta), same shapes as on `AccountOut`.
 *
 *  The event MAY fire with an UNCHANGED `state`: attention is time-based
 *  (`connecting` for longer than the hub's stuck threshold becomes
 *  `connecting_stuck` without any state change), so the projector patches
 *  every field regardless. `status_since` / `attention` are
 *  `.nullable().optional()`: a pre-0015 hub omits them. */
export const accountStatusPayloadSchema = z.object({
  account_id: z.string(),
  state: z.string(),
  detail: z.string().nullable().optional(),
  status_since: z.string().nullable().optional(),
  attention: accountAttentionSchema.nullable().optional(),
});

// --- Envelope -------------------------------------------------------------

/** The closed set of event-type discriminators — the backend's `EVENT_TYPES`
 *  (`hub/app/realtime/events.py`) minus `session.superseded`, a server-
 *  internal kick signal that is never forwarded to a client. This list (via
 *  `realtimeEventSchema` below) is the ONLY gate in `connection.ts`: every
 *  envelope that parses is forwarded — there is no second, hand-kept
 *  allowlist to drift (bug 6 dropped `message.reaction` exactly that way). */
export const EVENT_TYPES = [
  'message.new',
  'message.status',
  'message.reaction',
  'conversation.updated',
  'conversation.lock',
  'presence',
  'account.status',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

/** Discriminated union over `type` so a `switch` narrows `payload`. */
export const realtimeEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('message.new'),
    seq: z.number().int(),
    payload: messageNewPayloadSchema,
  }),
  z.object({
    type: z.literal('message.status'),
    seq: z.number().int(),
    payload: messageStatusPayloadSchema,
  }),
  z.object({
    type: z.literal('message.reaction'),
    seq: z.number().int(),
    payload: messageReactionPayloadSchema,
  }),
  z.object({
    type: z.literal('conversation.updated'),
    seq: z.number().int(),
    payload: conversationUpdatedPayloadSchema,
  }),
  z.object({
    type: z.literal('conversation.lock'),
    seq: z.number().int(),
    payload: conversationLockPayloadSchema,
  }),
  z.object({
    type: z.literal('presence'),
    seq: z.number().int(),
    payload: presencePayloadSchema,
  }),
  z.object({
    type: z.literal('account.status'),
    seq: z.number().int(),
    payload: accountStatusPayloadSchema,
  }),
]);

export type RealtimeEvent = z.infer<typeof realtimeEventSchema>;

// --- Server frames that are NOT event envelopes ---------------------------

/** `{"type":"pong"}` — server reply to our `ping`. No seq, no payload. */
export const pongFrameSchema = z.object({
  type: z.literal('pong'),
});

/** `{"type":"replay.gap","detail":"refetch_required"}` — server tells us the
 *  reconnect buffer didn't have enough history to cover our `last_seq`. The
 *  client must refetch the affected caches. */
export const replayGapFrameSchema = z.object({
  type: z.literal('replay.gap'),
  detail: z.string().optional(),
});

/** Union of every legal server frame we might receive on the open socket. */
export const serverFrameSchema = z.union([
  realtimeEventSchema,
  pongFrameSchema,
  replayGapFrameSchema,
]);

export type ServerFrame = z.infer<typeof serverFrameSchema>;
